import { digits, letters } from "../utils/utility-types.js";
import { formatUtcTime, isString, unsafeKeys } from "../utils/utility-functions.js";
import { formatRoute } from "../utils/formatRoute.js";
import { StringEnum, Type } from "@feathersjs/typebox";
import { NullableSchema } from "../typebox.js";
export const cidLetters = letters.filter((l) => l !== "I" && l !== "O");
export const cidCharacters = [...cidLetters, ...digits];
export const activeCoordinationTimeTypes = ["D", "E"];
export class EramFlightplan {
    isDroneFp;
    constructor(id, isDroneFp = false, extraFields = {}) {
        this.isDroneFp = isDroneFp;
        this.id = id.toUpperCase();
        Object.assign(this, extraFields);
        const now = Date.now();
        this.dateCreated = now;
        this.dateUpdated = now;
    }
    id;
    correlatedTrackId = null;
    isEmpty = true;
    flightRef = null;
    cid = null;
    callsign = "";
    squawk = null;
    reassignedSquawk = null;
    actualSpeed = null;
    dateCreated;
    dateUpdated;
    aircraftType = null;
    aircraftAddress = null;
    alternate = [];
    departure = "ZZZZ";
    destination = "ZZZZ";
    coordinationFix = null;
    equipmentQualifier = null;
    flightRules = "IFR";
    flightType = "GENERAL";
    requestedAirspeed = null;
    requestedAltitude = null;
    reportedAltitude = null;
    assignedAltitude = null;
    numOfAircraft = 1;
    persons = 1;
    remarks = "";
    localRemarks = "";
    // route in FAA format
    route = "";
    routeIsTruncated = false;
    convertedRoute = null;
    previousRoute = null;
    probeInhibit = null;
    appliedAdr = null;
    appliedAar = null;
    applicableADRs = [];
    applicableAARs = [];
    tfmReroute = null;
    status = "PROPOSED";
    performanceCat = "C";
    wakeTurbCategory = "L";
    communicationCodes = [];
    otherDatalinkCapabilities = [];
    navigationCodes = [];
    pbnCodes = [];
    otherNavigationCapabilities = [];
    surveillanceCodes = [];
    otherSurveillanceCapabilities = [];
    selectiveCallingCode = "";
    operator = "";
    other = "";
    registration = "";
    // Initial gate time of departure
    igtd = null;
    // Estimated time of arrival
    eta = null;
    landingTime = null;
    sta = null;
    std = null;
    // Estimated time of departure
    etd = null;
    static isRvsmEquipped(fp) {
        return fp.navigationCodes?.includes("W");
    }
    static isSatCommEquipped(fp) {
        return fp.communicationCodes?.some((c) => ["J5", "J7"].includes(c));
    }
    static isVFR(fp) {
        return (fp.assignedAltitude && Object.entries(fp.assignedAltitude).some(([k, v]) => k.startsWith("vfr") && v));
    }
    /**
     * mutates the input flightplan
     * returns the updated fields
     * @param fp
     * @param d
     */
    static update(fp, d) {
        const changedFields = ["dateUpdated"];
        const prevRoute = { route: fp.route, departure: fp.departure, destination: fp.destination };
        if (typeof d === "function") {
            const newFp = d(fp);
            changedFields.push(...unsafeKeys(newFp).filter((k) => newFp[k] !== fp[k]));
            Object.assign(fp, newFp);
        }
        else {
            changedFields.push(...Object.keys(d));
            Object.assign(fp, d);
        }
        if ("route" in changedFields) {
            fp.previousRoute = prevRoute;
            changedFields.push("previousRoute");
        }
        fp.isEmpty = false;
        fp.dateUpdated = Date.now();
        return Object.fromEntries(changedFields.map((k) => [k, fp[k]]));
    }
    static createFromPosconFlightplan(fp) {
        const eramFp = new EramFlightplan(fp.id);
        eramFp.callsign = fp.fp_callsign ?? "";
        eramFp.aircraftType = fp.fp_aircrafttype ?? "";
        eramFp.alternate = [fp.fp_alternate, fp.fp_alternate2].filter(isString);
        eramFp.departure = fp.fp_departure ?? "";
        eramFp.destination = fp.fp_destination ?? "";
        eramFp.route = fp.fp_route ? formatRoute(fp.fp_route) : "";
        eramFp.equipmentQualifier = fp.fp_faaterminalequipmentsuffix ?? null;
        eramFp.numOfAircraft = fp.fp_numOfAircraft ?? 1;
        eramFp.persons = Number(fp.fp_persons ?? "1");
        eramFp.remarks = fp.fp_remarks ?? "";
        eramFp.pbnCodes = fp.fp_pbn?.split(",") ?? [];
        eramFp.performanceCat = fp.fp_performanceCat ?? "C";
        eramFp.wakeTurbCategory = fp.fp_waketurbcategory ?? "L";
        eramFp.sta = fp.date_sta ? new Date(fp.date_sta).getTime() : null;
        eramFp.std = fp.date_std ? new Date(fp.date_std).getTime() : null;
        if (fp.fp_level && /^[AF]\d+$/.test(fp.fp_level)) {
            eramFp.requestedAltitude = Number.parseInt(fp.fp_level.slice(1), 10);
            eramFp.assignedAltitude = {
                simple: Number.parseInt(fp.fp_level.slice(1), 10),
            };
        }
        return eramFp;
    }
    static createPosconFlightplan(fp) {
        return {
            id: fp.id,
            active: true,
            date_created: new Date(fp.dateCreated).toJSON(),
            date_updated: new Date(fp.dateCreated).toJSON(),
            fp_callsign: fp.callsign,
            fp_aircrafttype: fp.aircraftType ?? "",
            fp_alternate: fp.alternate[0],
            fp_alternate2: fp.alternate[1],
            fp_departure: fp.departure,
            fp_destination: fp.destination,
            fp_route: fp.route,
            fp_faaterminalequipmentsuffix: fp.equipmentQualifier ?? undefined,
            fp_numOfAircraft: fp.numOfAircraft,
            fp_persons: fp.persons.toString(),
            fp_remarks: fp.remarks,
            fp_pbn: fp.pbnCodes.join(","),
            fp_performanceCat: fp.performanceCat,
            fp_waketurbcategory: fp.wakeTurbCategory,
            date_sta: fp.sta ? new Date(fp.sta).toJSON() : undefined,
            date_std: fp.std ? new Date(fp.std).toJSON() : undefined,
            fp_level: fp.assignedAltitude?.simple ? `F${fp.assignedAltitude.simple}` : undefined,
        };
    }
}
export const excludedGlobalEramFlightplanFields = [
    "cid",
    "coordinationFix",
    "squawk",
    "reassignedSquawk",
    "localRemarks",
];
export function formatNasAltitude(alt) {
    if (alt.simple) {
        return alt.simple.toFixed(0);
    }
    else if (alt.block) {
        return `${alt.block.min}B${alt.block.max}`;
    }
    else if (alt.altFixAlt) {
        return `${alt.altFixAlt.pre}/${alt.altFixAlt.fix}/${alt.altFixAlt.post}`;
    }
    else if (alt.above) {
        return `ABV/${alt.above}`;
    }
    else if (alt.vfrOnTop) {
        return "OTP";
    }
    else if (alt.vfrPlus) {
        return `VFR/${alt.vfrPlus}`;
    }
    else if (alt.vfrOnTopPlus) {
        return `OTP/${alt.vfrOnTopPlus}`;
    }
    else if (alt.vfr) {
        return "VFR";
    }
    return "";
}
export function formatSpeed(speed) {
    return `${speed.uom === "MACH" ? "M" : ""}${speed.value}`;
}
export function formatCoordinationTime(fix) {
    return `${fix.type}${formatUtcTime(fix.time)}`;
}
function createUomSchema(units, valueType, nullable = false) {
    const TObj = Type.Object({
        uom: StringEnum(units),
        value: valueType,
    });
    return nullable ? NullableSchema(TObj) : TObj;
}
//# sourceMappingURL=eramFlightplan.js.map